import { useState, useEffect } from "react";
import { Navigate, useParams } from "react-router";
import { Globals } from "../globals";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Container from "react-bootstrap/esm/Container";
import Card from "react-bootstrap/esm/Card";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import ListGroup from "react-bootstrap/esm/ListGroup";
import AppFooter from "../footer/AppFooter";
import BiblePassageInput from "./BiblePassageInput";
import { BIBLE_BOOKS, assembleBiblePassage, parseBiblePassage } from "../utils/bibleBooks";

// The token form is public, so the server only sends a key and a display name.
interface PreacherInfo {
    key: string;
    displayName: string;
}

interface SermonInfoData {
    serviceDate: string;
    serviceType?: string;
    serviceLabel?: string;
    sermonSlot: 1 | 2;
    sermonMode?: "sermon" | "intro" | "reminder" | "choir";
    requiresSermonDetails?: boolean;
    preacher: PreacherInfo | null;
    currentTopic: string;
    currentBiblePassage: string;
    currentPrayerConcern?: string;
    confirmedAt?: string | null;
    preachers: PreacherInfo[];
    // False when the Gemeinde switched the direct handover off (missing = allowed).
    allowPreacherChange?: boolean;
    // Choir-song mode (GOD-2)
    choirSongCount?: number;
    currentChoirSongs?: string[];
    // The conductor's personal page; a choir reminder link forwards there.
    choirPlanToken?: string;
}

type FormState = "loading" | "form" | "change-preacher" | "submitted" | "error";

export default function SermonInfoForm() {
    const { token } = useParams<{ token: string }>();
    const { t } = useTranslation();

    const [formState, setFormState] = useState<FormState>("loading");
    const [errorMessage, setErrorMessage] = useState("");
    const [sermonData, setSermonData] = useState<SermonInfoData | null>(null);

    // Form fields
    const [topic, setTopic] = useState("");
    // Wochenschluss prayer slot (sermonMode === "intro")
    const [prayerConcern, setPrayerConcern] = useState("");
    // Choir songs (sermonMode === "choir")
    const [choirSongs, setChoirSongs] = useState<string[]>([]);

    // Bible passage structured fields
    const [bookInput, setBookInput] = useState("");
    const [selectedBook, setSelectedBook] = useState("");
    const [chapter, setChapter] = useState("");
    const [verses, setVerses] = useState("");
    const [chapterError, setChapterError] = useState("");

    // Change preacher
    const [selectedNewPreacher, setSelectedNewPreacher] = useState("");
    const [changingPreacher, setChangingPreacher] = useState(false);
    // Set after a successful handover: was the new preacher notified automatically?
    const [handoverNotified, setHandoverNotified] = useState<boolean | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = t("sermonInfo.title");
    }, [t]);

    useEffect(() => {
        if (!token) {
            setFormState("error");
            setErrorMessage(t("sermonInfo.invalidLink") as string);
            return;
        }

        const getSermonInfo = httpsCallable<{ tokenId: string }, SermonInfoData>(
            functions,
            "getSermonInfoByToken"
        );

        getSermonInfo({ tokenId: token })
            .then((result) => {
                setSermonData(result.data);
                setTopic(result.data.currentTopic || "");
                setPrayerConcern(result.data.currentPrayerConcern || "");
                if (result.data.sermonMode === "choir") {
                    const count = result.data.choirSongCount || 4;
                    const existing = result.data.currentChoirSongs || [];
                    setChoirSongs(Array.from({ length: count }, (_, i) => existing[i] || ""));
                }
                // Parse existing Bible passage string into structured fields
                const parsed = parseBiblePassage(result.data.currentBiblePassage || "");
                if (parsed.book && BIBLE_BOOKS.includes(parsed.book)) {
                    setBookInput(parsed.book);
                    setSelectedBook(parsed.book);
                    setChapter(parsed.chapter);
                    setVerses(parsed.verses);
                }
                setFormState("form");
            })
            .catch((err) => {
                setFormState("error");
                if (err.code === "functions/failed-precondition") {
                    setErrorMessage(t("sermonInfo.alreadyUsed") as string);
                } else if (err.code === "functions/deadline-exceeded") {
                    setErrorMessage(t("sermonInfo.expired") as string);
                } else if (err.code === "functions/not-found") {
                    setErrorMessage(t("sermonInfo.notFound") as string);
                } else {
                    setErrorMessage(t("errors.anErrorOccurred") as string);
                }
            });
    }, [token, t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!sermonData) {
            return;
        }

        const biblePassage = assembleBiblePassage(selectedBook, chapter, verses);
        const isChoirMode = sermonData.sermonMode === "choir";
        const isPrayerMode = sermonData.sermonMode === "intro";
        const requiresSermonDetails =
            sermonData.requiresSermonDetails !== false && !isPrayerMode && !isChoirMode;

        if (requiresSermonDetails) {
            if (!topic && !biblePassage) {
                return;
            }

            if (selectedBook && chapterError) {
                return;
            }
        }

        if (isPrayerMode && !prayerConcern.trim()) {
            return;
        }

        if (isChoirMode && !choirSongs.some((song) => song.trim())) {
            return;
        }

        setSubmitting(true);
        try {
            const submitSermonInfo = httpsCallable<
                { tokenId: string; topic: string; biblePassage: string; prayerConcern: string; choirSongs?: string[] },
                { success: boolean }
            >(functions, "submitSermonInfo");

            await submitSermonInfo({
                tokenId: token!,
                topic: requiresSermonDetails ? topic : "",
                biblePassage: requiresSermonDetails ? biblePassage : "",
                prayerConcern: isPrayerMode ? prayerConcern.trim() : "",
                ...(isChoirMode ? { choirSongs: choirSongs.map((song) => song.trim()) } : {}),
            });

            setFormState("submitted");
        } catch (err: unknown) {
            const firebaseErr = err as { code?: string };
            if (firebaseErr.code === "functions/failed-precondition") {
                setFormState("error");
                setErrorMessage(t("sermonInfo.alreadyUsed") as string);
            } else {
                setFormState("error");
                setErrorMessage(t("errors.anErrorOccurred") as string);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangePreacher = async () => {
        if (!selectedNewPreacher) return;

        setChangingPreacher(true);
        try {
            const changePreacher = httpsCallable<
                { tokenId: string; newPreacherKey: string },
                { success: boolean; notified?: boolean; reason?: string }
            >(functions, "changeSermonPreacher");

            const result = await changePreacher({
                tokenId: token!,
                newPreacherKey: selectedNewPreacher,
            });

            setHandoverNotified(result.data.notified !== false);
            setFormState("submitted");
        } catch (err: unknown) {
            const firebaseErr = err as { code?: string };
            if (firebaseErr.code === "functions/failed-precondition") {
                setFormState("error");
                setErrorMessage(t("sermonInfo.alreadyUsed") as string);
            } else {
                setFormState("error");
                setErrorMessage(t("errors.anErrorOccurred") as string);
            }
        } finally {
            setChangingPreacher(false);
        }
    };

    const formatDate = (isoDate: string) => {
        return new Date(isoDate).toLocaleDateString(t("calendar.locale") as string, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // The choir reminder leads to the conductor's personal page (catalogue,
    // repeat warning, all coming services) instead of this one-time form.
    if (sermonData?.sermonMode === "choir" && sermonData.choirPlanToken) {
        return <Navigate to={`${Globals.PATH_CHOIR_PLAN}/${sermonData.choirPlanToken}`} replace />;
    }

    if (formState === "loading") {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
                <p className="mt-2">{t("common.loading")}</p>
            </Container>
        );
    }

    if (formState === "error") {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="danger">
                    <Alert.Heading>{t("sermonInfo.errorTitle")}</Alert.Heading>
                    <p>{errorMessage}</p>
                </Alert>
            </Container>
        );
    }

    if (formState === "submitted" && handoverNotified !== null) {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="success">
                    <Alert.Heading>{t("sermonInfo.handoverTitle")}</Alert.Heading>
                    <p>{t(handoverNotified ? "sermonInfo.handoverNotified" : "sermonInfo.handoverSaved")}</p>
                </Alert>
                {!handoverNotified && <Alert variant="warning">{t("sermonInfo.handoverNotNotified")}</Alert>}
            </Container>
        );
    }

    if (formState === "submitted") {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="success">
                    <Alert.Heading>{t("sermonInfo.successTitle")}</Alert.Heading>
                    <p>{t("sermonInfo.successMessage")}</p>
                </Alert>
            </Container>
        );
    }

    if (!sermonData) return null;

    const preacherName = sermonData.preacher?.displayName || t("sermonInfo.unknownPreacher");

    const slotLabel =
        sermonData.sermonMode === "intro"
            ? t("sermonInfo.introSlot")
            : sermonData.sermonMode === "reminder"
                ? t("sermonInfo.reminderSlot")
                : sermonData.sermonSlot === 1
                    ? t("sermonInfo.firstSermon")
                    : t("sermonInfo.secondSermon");

    const isChoirMode = sermonData.sermonMode === "choir";
    const isPrayerMode = sermonData.sermonMode === "intro";
    const requiresSermonDetails =
        sermonData.requiresSermonDetails !== false && !isPrayerMode && !isChoirMode;
    const heading = isChoirMode ? t("sermonInfo.choirHeading") : t("sermonInfo.heading");

    // Other preachers (exclude current preacher)
    const otherPreachers = sermonData.preachers.filter(
        (p) => p.key !== sermonData.preacher?.key
    );

    return (
        <Container className="mt-4" style={{ maxWidth: "600px" }}>
            <Card>
                <Card.Header>
                    <h4 className="mb-0">{heading}</h4>
                </Card.Header>
                <Card.Body>
                    {/* Service info */}
                    <ListGroup variant="flush" className="mb-4">
                        <ListGroup.Item>
                            <strong>{t("sermonInfo.date")}:</strong>{" "}
                            {formatDate(sermonData.serviceDate)}
                        </ListGroup.Item>
                        {sermonData.serviceLabel && (
                            <ListGroup.Item>
                                <strong>{t("sermonInfo.serviceType")}:</strong> {sermonData.serviceLabel}
                            </ListGroup.Item>
                        )}
                        {!isChoirMode && (
                            <>
                                <ListGroup.Item>
                                    <strong>{t("sermonInfo.sermon")}:</strong> {slotLabel}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>{t("sermonInfo.preacher")}:</strong> {preacherName}
                                </ListGroup.Item>
                            </>
                        )}
                    </ListGroup>

                    {formState === "form" && (
                        <>
                            <Form onSubmit={handleSubmit}>
                                {requiresSermonDetails ? (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t("sermonInfo.topic")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={topic}
                                                onChange={(e) => setTopic(e.target.value)}
                                                placeholder={t("sermonInfo.topicPlaceholder") as string}
                                            />
                                        </Form.Group>

                                        <Form.Label>{t("sermonInfo.biblePassage")}</Form.Label>
                                        <BiblePassageInput
                                            bookInput={bookInput}
                                            selectedBook={selectedBook}
                                            chapter={chapter}
                                            verses={verses}
                                            chapterError={chapterError}
                                            onBookChange={(input, book) => { setBookInput(input); setSelectedBook(book); }}
                                            onChapterChange={setChapter}
                                            onVersesChange={setVerses}
                                            onChapterErrorChange={setChapterError}
                                            idSuffix="-sermon-info"
                                        />
                                    </>
                                ) : isChoirMode ? (
                                    <>
                                        {choirSongs.map((song, i) => (
                                            <Form.Group className="mb-3" key={i}>
                                                <Form.Label>
                                                    {t("sermonInfo.choirSongLabel", { number: i + 1 })}
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={song}
                                                    onChange={(e) =>
                                                        setChoirSongs((prev) =>
                                                            prev.map((s, idx) => (idx === i ? e.target.value : s))
                                                        )
                                                    }
                                                    placeholder={t("sermonInfo.choirSongPlaceholder") as string}
                                                />
                                            </Form.Group>
                                        ))}
                                        <Form.Text className="text-muted">{t("sermonInfo.choirHelp")}</Form.Text>
                                    </>
                                ) : isPrayerMode ? (
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("sermonInfo.prayerConcern")}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={prayerConcern}
                                            onChange={(e) => setPrayerConcern(e.target.value)}
                                            placeholder={t("sermonInfo.prayerConcernPlaceholder") as string}
                                        />
                                        <Form.Text className="text-muted">
                                            {t("sermonInfo.prayerConcernHelp")}
                                        </Form.Text>
                                    </Form.Group>
                                ) : (
                                    <Alert variant="info" className="mb-3">
                                        {t("sermonInfo.reminderOnlyHelp")}
                                    </Alert>
                                )}

                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={
                                            submitting ||
                                            (requiresSermonDetails && !topic && !assembleBiblePassage(selectedBook, chapter, verses)) ||
                                            (requiresSermonDetails && !!chapterError) ||
                                            (isPrayerMode && !prayerConcern.trim()) ||
                                            (isChoirMode && !choirSongs.some((song) => song.trim()))
                                        }
                                    >
                                        {submitting ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                {t("common.loading")}
                                            </>
                                        ) : requiresSermonDetails ? (
                                            t("sermonInfo.submitButton")
                                        ) : isChoirMode ? (
                                            t("sermonInfo.choirSubmitButton")
                                        ) : isPrayerMode ? (
                                            t("sermonInfo.prayerSubmitButton")
                                        ) : (
                                            t("sermonInfo.confirmButton")
                                        )}
                                    </Button>

                                    {sermonData.allowPreacherChange === false && !isChoirMode && (
                                        <p className="text-muted small mt-3 mb-0">{t("sermonInfo.swapInsteadHint")}</p>
                                    )}
                                    {sermonData.allowPreacherChange !== false && otherPreachers.length > 0 && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setFormState("change-preacher")}
                                        >
                                            {t("sermonInfo.changePreacherButton")}
                                        </Button>
                                    )}
                                </div>
                            </Form>
                        </>
                    )}

                    {formState === "change-preacher" && (
                        <>
                            <p className="text-muted mb-3">{t("sermonInfo.changePreacherInfo")}</p>
                            <Form.Group className="mb-3">
                                <Form.Label>{t("sermonInfo.selectNewPreacher")}</Form.Label>
                                <Form.Select
                                    value={selectedNewPreacher}
                                    onChange={(e) => setSelectedNewPreacher(e.target.value)}
                                >
                                    <option value="">{t("sermonInfo.selectPreacherPlaceholder")}</option>
                                    {otherPreachers.map((p) => (
                                        <option key={p.key} value={p.key}>
                                            {p.displayName}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <div className="d-flex gap-2">
                                <Button
                                    variant="warning"
                                    onClick={handleChangePreacher}
                                    disabled={!selectedNewPreacher || changingPreacher}
                                >
                                    {changingPreacher ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                className="me-2"
                                            />
                                            {t("common.loading")}
                                        </>
                                    ) : (
                                        t("sermonInfo.confirmChangePreacher")
                                    )}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setFormState("form")}
                                >
                                    {t("common.cancel")}
                                </Button>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            <p className="text-muted text-center mt-3" style={{ fontSize: "0.85em" }}>
                {t("sermonInfo.linkValidity")}
            </p>
            <AppFooter />
        </Container>
    );
}
